"use client";
import {
  AccountRecovery,
  type AccountRecoveryState,
} from "../account-recovery";
import {
  Alert,
  Button,
  Dialog,
  Input,
  Stack,
  Text,
} from "@softmax/design-system";
import { spacing } from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { useId, useRef, type ReactNode, type RefObject } from "react";
const styles = stylex.create({
  field: { display: "grid", gap: spacing.space8 },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: spacing.space12,
  },
});
export function AccountPlayerDialog({
  open,
  mode,
  name,
  pending,
  error,
  recovery,
  onRefresh,
  finalFocusRef,
  onNameChange,
  onClose,
  onSubmit,
  disabled = false,
  notice,
}: {
  readonly open: boolean;
  readonly mode: "create" | "rename";
  readonly name: string;
  readonly pending: boolean;
  readonly error?: string;
  readonly recovery?: AccountRecoveryState;
  readonly onRefresh?: () => void;
  readonly finalFocusRef?: RefObject<HTMLElement | null>;
  readonly onNameChange: (name: string) => void;
  readonly onClose: () => void;
  readonly onSubmit: () => void;
  readonly disabled?: boolean;
  readonly notice?: ReactNode;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  return (
    <Dialog
      open={open}
      title={mode === "create" ? "Create player" : "Rename player"}
      finalFocusRef={finalFocusRef}
      initialFocusRef={input}
      onOpenChange={(next) => {
        if (!next && !pending) onClose();
      }}
    >
      {notice}
      <form
        onSubmit={(event) => {
          event.preventDefault();

          if (!disabled && !pending && !recovery) onSubmit();
        }}
      >
        <Stack width="auto">
          <label htmlFor={id} {...stylex.props(styles.field)}>
            Player name
            <Input
              ref={input}
              id={id}
              value={name}
              required
              maxLength={100}
              disabled={disabled || pending}
              onChange={(event) => onNameChange(event.target.value)}
            />
          </label>
          {error ? (
            <Alert tone="danger" announcement="assertive">
              {error}
            </Alert>
          ) : null}
          {recovery ? (
            <Stack width="auto" gap={8}>
              <AccountRecovery
                recovery={recovery}
                resource="players"
                onRefresh={onRefresh}
              />
            </Stack>
          ) : null}
          {pending ? (
            <output>
              <Text>Saving player… Please wait before closing.</Text>
            </output>
          ) : null}
          <div {...stylex.props(styles.actions)}>
            <Button type="button" disabled={pending} onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={disabled || pending || !!recovery || !name.trim()}
            >
              {mode === "create" ? "Create player" : "Save name"}
            </Button>
          </div>
        </Stack>
      </form>
    </Dialog>
  );
}
